import * as THREE from 'three';
import { createCelMesh } from './shaders/CelShader.js';

// House exterior model - Cozy cottage style
export function createHouse(scene, position = new THREE.Vector3(20, 0, 20)) {
  const houseGroup = new THREE.Group();

  // === BLOB SHADOW ===
  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = 64;
  shadowCanvas.height = 64;
  const shadowCtx = shadowCanvas.getContext('2d');
  const gradient = shadowCtx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0.3)');
  gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.15)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  shadowCtx.fillStyle = gradient;
  shadowCtx.fillRect(0, 0, 64, 64);
  const shadowTexture = new THREE.CanvasTexture(shadowCanvas);
  const houseShadow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: shadowTexture,
    transparent: true,
    depthWrite: false
  }));
  houseShadow.scale.set(10, 8, 1); // Rectangular-ish shadow for house
  houseShadow.position.set(0, 0.02, 0.5);
  houseGroup.add(houseShadow);

  // === FOUNDATION ===
  const foundationGeometry = new THREE.BoxGeometry(5.6, 0.3, 4.6);
  const foundation = createCelMesh(foundationGeometry, 0x696969); // Gray stone
  foundation.position.y = 0.15;
  houseGroup.add(foundation);

  // === MAIN WALLS ===
  const wallsGeometry = new THREE.BoxGeometry(5, 3, 4);
  const walls = createCelMesh(wallsGeometry, 0xFFF8DC); // Cream/cornsilk color
  walls.position.y = 1.8;
  houseGroup.add(walls);

  // Wall trim (bottom)
  const trimGeometry = new THREE.BoxGeometry(5.2, 0.15, 4.2);
  const trim = createCelMesh(trimGeometry, 0x8B4513); // Brown trim
  trim.position.y = 0.38;
  houseGroup.add(trim);

  // Wall trim (top)
  const topTrimGeometry = new THREE.BoxGeometry(5.2, 0.1, 4.2);
  const topTrim = createCelMesh(topTrimGeometry, 0x8B4513);
  topTrim.position.y = 3.25;
  houseGroup.add(topTrim);

  // === ROOF ===
  // Main roof (steeper, more cottage-like)
  const roofGeometry = new THREE.ConeGeometry(4.2, 2.5, 4);
  const roof = createCelMesh(roofGeometry, 0xB22222); // Firebrick red
  roof.position.y = 4.55;
  roof.rotation.y = Math.PI / 4;
  houseGroup.add(roof);

  // Roof overhang/eaves
  const eavesGeometry = new THREE.BoxGeometry(5.8, 0.15, 4.8);
  const eaves = createCelMesh(eavesGeometry, 0x8B4513);
  eaves.position.y = 3.35;
  houseGroup.add(eaves);

  // === CHIMNEY ===
  const chimneyGroup = new THREE.Group();

  const chimneyGeometry = new THREE.BoxGeometry(0.7, 2, 0.7);
  const chimney = createCelMesh(chimneyGeometry, 0xA0522D); // Sienna brown brick
  chimney.position.y = 1;
  chimneyGroup.add(chimney);

  // Chimney cap
  const chimneyCapGeometry = new THREE.BoxGeometry(0.9, 0.15, 0.9);
  const chimneyCap = createCelMesh(chimneyCapGeometry, 0x696969);
  chimneyCap.position.y = 2.1;
  chimneyGroup.add(chimneyCap);

  // Chimney top detail
  const chimneyTopGeometry = new THREE.BoxGeometry(0.5, 0.2, 0.5);
  const chimneyTop = createCelMesh(chimneyTopGeometry, 0x696969);
  chimneyTop.position.y = 2.25;
  chimneyGroup.add(chimneyTop);

  chimneyGroup.position.set(1.8, 3.5, -1);
  houseGroup.add(chimneyGroup);

  // === FRONT PORCH ===
  const porchGroup = new THREE.Group();

  // Porch floor
  const porchFloorGeometry = new THREE.BoxGeometry(3, 0.2, 1.5);
  const porchFloor = createCelMesh(porchFloorGeometry, 0x8B4513);
  porchFloor.position.set(0, 0.1, 2.75);
  porchGroup.add(porchFloor);

  // Porch steps
  const step1Geometry = new THREE.BoxGeometry(1.8, 0.15, 0.4);
  const step1 = createCelMesh(step1Geometry, 0x8B4513);
  step1.position.set(0, 0.075, 3.7);
  porchGroup.add(step1);

  const step2Geometry = new THREE.BoxGeometry(1.6, 0.15, 0.4);
  const step2 = createCelMesh(step2Geometry, 0x8B4513);
  step2.position.set(0, -0.05, 4.1);
  porchGroup.add(step2);

  // Porch posts
  const postGeometry = new THREE.CylinderGeometry(0.08, 0.1, 1.8, 8);

  const leftPost = createCelMesh(postGeometry, 0xF5F5DC); // Beige
  leftPost.position.set(-1.3, 1.1, 2.75);
  porchGroup.add(leftPost);

  const rightPost = createCelMesh(postGeometry, 0xF5F5DC);
  rightPost.position.set(1.3, 1.1, 2.75);
  porchGroup.add(rightPost);

  // Porch roof/overhang
  const porchRoofGeometry = new THREE.BoxGeometry(3.2, 0.15, 1.8);
  const porchRoof = createCelMesh(porchRoofGeometry, 0xB22222);
  porchRoof.position.set(0, 2.1, 2.9);
  porchRoof.rotation.x = -0.1; // Slight angle
  porchGroup.add(porchRoof);

  houseGroup.add(porchGroup);

  // === DOOR ===
  const doorGroup = new THREE.Group();

  // Door frame
  const doorFrameGeometry = new THREE.BoxGeometry(1.4, 2.2, 0.1);
  const doorFrame = createCelMesh(doorFrameGeometry, 0x5D4037);
  doorFrame.position.set(0, 1.1, 2.01);
  doorGroup.add(doorFrame);

  // Main door
  const doorGeometry = new THREE.BoxGeometry(1.2, 2, 0.15);
  const door = createCelMesh(doorGeometry, 0x6D4C41); // Lighter brown
  door.position.set(0, 1.05, 2.05);
  doorGroup.add(door);

  // Door panels (decorative)
  const panelGeometry = new THREE.BoxGeometry(0.45, 0.7, 0.02);
  const panelMaterial = new THREE.MeshStandardMaterial({ color: 0x4E342E });

  const panel1 = new THREE.Mesh(panelGeometry, panelMaterial);
  panel1.position.set(-0.28, 1.5, 2.14);
  doorGroup.add(panel1);

  const panel2 = new THREE.Mesh(panelGeometry, panelMaterial);
  panel2.position.set(0.28, 1.5, 2.14);
  doorGroup.add(panel2);

  const panel3 = new THREE.Mesh(panelGeometry, panelMaterial);
  panel3.position.set(-0.28, 0.6, 2.14);
  doorGroup.add(panel3);

  const panel4 = new THREE.Mesh(panelGeometry, panelMaterial);
  panel4.position.set(0.28, 0.6, 2.14);
  doorGroup.add(panel4);

  // Door handle
  const handleGeometry = new THREE.SphereGeometry(0.08, 8, 8);
  const handleMaterial = new THREE.MeshStandardMaterial({
    color: 0xFFD700,
    roughness: 0.3,
    metalness: 0.9
  });
  const handle = new THREE.Mesh(handleGeometry, handleMaterial);
  handle.position.set(0.45, 1.05, 2.18);
  doorGroup.add(handle);

  // Door knocker
  const knockerRingGeometry = new THREE.TorusGeometry(0.08, 0.015, 8, 16);
  const knockerRing = new THREE.Mesh(knockerRingGeometry, handleMaterial);
  knockerRing.position.set(0, 1.4, 2.18);
  doorGroup.add(knockerRing);

  houseGroup.add(doorGroup);

  // === WINDOWS WITH FRAMES AND SHUTTERS ===
  const createWindow = (x, y, z, rotationY = 0) => {
    const windowGroup = new THREE.Group();

    // Window frame
    const frameGeometry = new THREE.BoxGeometry(1.0, 1.0, 0.1);
    const frame = createCelMesh(frameGeometry, 0x5D4037);
    windowGroup.add(frame);

    // Window glass
    const glassGeometry = new THREE.PlaneGeometry(0.8, 0.8);
    const glassMaterial = new THREE.MeshStandardMaterial({
      color: 0x87CEEB,
      roughness: 0.1,
      metalness: 0.3,
      transparent: true,
      opacity: 0.7
    });
    const glass = new THREE.Mesh(glassGeometry, glassMaterial);
    glass.position.z = 0.06;
    windowGroup.add(glass);

    // Window cross bars
    const barGeometry = new THREE.BoxGeometry(0.04, 0.8, 0.05);
    const verticalBar = createCelMesh(barGeometry, 0xF5F5DC);
    verticalBar.position.z = 0.08;
    windowGroup.add(verticalBar);

    const horizBar = createCelMesh(new THREE.BoxGeometry(0.8, 0.04, 0.05), 0xF5F5DC);
    horizBar.position.z = 0.08;
    windowGroup.add(horizBar);

    // Shutters
    const shutterGeometry = new THREE.BoxGeometry(0.35, 0.9, 0.05);
    const leftShutter = createCelMesh(shutterGeometry, 0x2E7D32); // Green shutters
    leftShutter.position.set(-0.58, 0, 0);
    leftShutter.rotation.y = 0.2; // Slightly open
    windowGroup.add(leftShutter);

    const rightShutter = createCelMesh(shutterGeometry, 0x2E7D32);
    rightShutter.position.set(0.58, 0, 0);
    rightShutter.rotation.y = -0.2;
    windowGroup.add(rightShutter);

    // Window sill
    const sillGeometry = new THREE.BoxGeometry(1.1, 0.08, 0.2);
    const sill = createCelMesh(sillGeometry, 0xF5F5DC);
    sill.position.set(0, -0.54, 0.1);
    windowGroup.add(sill);

    windowGroup.position.set(x, y, z);
    windowGroup.rotation.y = rotationY;

    return windowGroup;
  };

  // Front windows
  houseGroup.add(createWindow(-1.6, 2.0, 2.01, 0));
  houseGroup.add(createWindow(1.6, 2.0, 2.01, 0));

  // Side windows
  houseGroup.add(createWindow(2.51, 2.0, 0, Math.PI / 2));
  houseGroup.add(createWindow(-2.51, 2.0, 0, -Math.PI / 2));

  // === FLOWER BOXES ===
  const createFlowerBox = (x, y, z, rotationY = 0) => {
    const boxGroup = new THREE.Group();

    // Box container
    const containerGeometry = new THREE.BoxGeometry(0.9, 0.25, 0.25);
    const container = createCelMesh(containerGeometry, 0x8B4513);
    boxGroup.add(container);

    // Dirt
    const dirtGeometry = new THREE.BoxGeometry(0.8, 0.1, 0.2);
    const dirt = createCelMesh(dirtGeometry, 0x3E2723);
    dirt.position.y = 0.08;
    boxGroup.add(dirt);

    // Flowers
    const flowerColors = [0xFF69B4, 0xFF6347, 0xFFD700, 0xFF1493, 0x9370DB];
    for (let i = 0; i < 5; i++) {
      const flowerGroup = new THREE.Group();

      // Stem
      const stemGeometry = new THREE.CylinderGeometry(0.015, 0.015, 0.25, 6);
      const stem = createCelMesh(stemGeometry, 0x228B22);
      stem.position.y = 0.2;
      flowerGroup.add(stem);

      // Flower head
      const petalGeometry = new THREE.SphereGeometry(0.06, 8, 8);
      const petal = createCelMesh(petalGeometry, flowerColors[i % flowerColors.length]);
      petal.position.y = 0.35;
      flowerGroup.add(petal);

      // Flower center
      const centerGeometry = new THREE.SphereGeometry(0.025, 6, 6);
      const center = createCelMesh(centerGeometry, 0xFFD700);
      center.position.y = 0.35;
      center.position.z = 0.04;
      flowerGroup.add(center);

      flowerGroup.position.x = -0.3 + i * 0.15;
      flowerGroup.position.y = 0.1;
      flowerGroup.rotation.z = (Math.random() - 0.5) * 0.3;
      boxGroup.add(flowerGroup);
    }

    boxGroup.position.set(x, y, z);
    boxGroup.rotation.y = rotationY;

    return boxGroup;
  };

  // Add flower boxes under front windows
  houseGroup.add(createFlowerBox(-1.6, 1.38, 2.15, 0));
  houseGroup.add(createFlowerBox(1.6, 1.38, 2.15, 0));

  // === STONE PATH ===
  const createStonePath = () => {
    const pathGroup = new THREE.Group();

    // Individual stepping stones
    const stonePositions = [
      { x: 0, z: 4.5 },
      { x: 0.15, z: 5.2 },
      { x: -0.1, z: 5.9 },
      { x: 0.1, z: 6.6 },
      { x: -0.15, z: 7.3 }
    ];

    stonePositions.forEach((pos, i) => {
      const size = 0.5 + Math.random() * 0.2;
      const stoneGeometry = new THREE.CylinderGeometry(size * 0.5, size * 0.55, 0.08, 7 + Math.floor(Math.random() * 3));
      const stone = createCelMesh(stoneGeometry, 0x808080);
      stone.position.set(pos.x, 0.04, pos.z);
      stone.rotation.y = Math.random() * Math.PI;
      pathGroup.add(stone);
    });

    return pathGroup;
  };

  houseGroup.add(createStonePath());

  // === FENCE ===
  const createFence = () => {
    const fenceGroup = new THREE.Group();

    const createFenceSection = (x, z, rotationY = 0) => {
      const sectionGroup = new THREE.Group();

      // Horizontal rails
      const railGeometry = new THREE.BoxGeometry(2, 0.08, 0.05);
      const topRail = createCelMesh(railGeometry, 0xF5F5DC);
      topRail.position.y = 0.7;
      sectionGroup.add(topRail);

      const bottomRail = createCelMesh(railGeometry, 0xF5F5DC);
      bottomRail.position.y = 0.3;
      sectionGroup.add(bottomRail);

      // Vertical pickets
      const picketGeometry = new THREE.BoxGeometry(0.08, 0.9, 0.04);
      for (let i = 0; i < 6; i++) {
        const picket = createCelMesh(picketGeometry, 0xF5F5DC);
        picket.position.set(-0.85 + i * 0.34, 0.5, 0);

        // Pointed top
        const pointGeometry = new THREE.ConeGeometry(0.055, 0.12, 4);
        const point = createCelMesh(pointGeometry, 0xF5F5DC);
        point.position.set(-0.85 + i * 0.34, 0.98, 0);
        point.rotation.y = Math.PI / 4;
        sectionGroup.add(point);

        sectionGroup.add(picket);
      }

      sectionGroup.position.set(x, 0, z);
      sectionGroup.rotation.y = rotationY;

      return sectionGroup;
    };

    // Front fence (with gap for path)
    fenceGroup.add(createFenceSection(-2.5, 5, 0));
    fenceGroup.add(createFenceSection(2.5, 5, 0));

    // Side fences
    fenceGroup.add(createFenceSection(-3.5, 3, Math.PI / 2));
    fenceGroup.add(createFenceSection(-3.5, 1, Math.PI / 2));
    fenceGroup.add(createFenceSection(3.5, 3, Math.PI / 2));
    fenceGroup.add(createFenceSection(3.5, 1, Math.PI / 2));

    return fenceGroup;
  };

  houseGroup.add(createFence());

  // === MAILBOX ===
  const mailboxGroup = new THREE.Group();

  // Post
  const mailPostGeometry = new THREE.CylinderGeometry(0.06, 0.06, 1.2, 8);
  const mailPost = createCelMesh(mailPostGeometry, 0x8B4513);
  mailPost.position.y = 0.6;
  mailboxGroup.add(mailPost);

  // Box
  const mailBoxGeometry = new THREE.BoxGeometry(0.4, 0.25, 0.2);
  const mailBox = createCelMesh(mailBoxGeometry, 0x2196F3); // Blue mailbox
  mailBox.position.y = 1.3;
  mailboxGroup.add(mailBox);

  // Rounded top
  const mailTopGeometry = new THREE.CylinderGeometry(0.1, 0.1, 0.4, 8);
  const mailTop = createCelMesh(mailTopGeometry, 0x2196F3);
  mailTop.position.y = 1.3;
  mailTop.rotation.z = Math.PI / 2;
  mailboxGroup.add(mailTop);

  // Flag
  const flagGeometry = new THREE.BoxGeometry(0.15, 0.08, 0.02);
  const flag = createCelMesh(flagGeometry, 0xFF0000);
  flag.position.set(0.2, 1.35, 0.12);
  flag.rotation.z = 0.3; // Flag up
  mailboxGroup.add(flag);

  mailboxGroup.position.set(2.2, 0, 5);
  houseGroup.add(mailboxGroup);

  // === LAMP POST ===
  const lampGroup = new THREE.Group();

  // Pole
  const poleGeometry = new THREE.CylinderGeometry(0.05, 0.08, 2, 8);
  const pole = createCelMesh(poleGeometry, 0x1A1A1A);
  pole.position.y = 1;
  lampGroup.add(pole);

  // Lamp housing
  const housingGeometry = new THREE.BoxGeometry(0.35, 0.4, 0.35);
  const housing = createCelMesh(housingGeometry, 0x1A1A1A);
  housing.position.y = 2.2;
  lampGroup.add(housing);

  // Lamp glass
  const lampGlassGeometry = new THREE.BoxGeometry(0.25, 0.3, 0.25);
  const lampGlassMaterial = new THREE.MeshStandardMaterial({
    color: 0xFFFACD,
    emissive: 0xFFD700,
    emissiveIntensity: 0.3,
    transparent: true,
    opacity: 0.8
  });
  const lampGlass = new THREE.Mesh(lampGlassGeometry, lampGlassMaterial);
  lampGlass.position.y = 2.2;
  lampGroup.add(lampGlass);

  // Lamp top
  const lampTopGeometry = new THREE.ConeGeometry(0.25, 0.2, 4);
  const lampTop = createCelMesh(lampTopGeometry, 0x1A1A1A);
  lampTop.position.y = 2.5;
  lampTop.rotation.y = Math.PI / 4;
  lampGroup.add(lampTop);

  lampGroup.position.set(-2.2, 0, 5);
  houseGroup.add(lampGroup);

  // === BUSHES ===
  const createBush = (x, z, scale = 1) => {
    const bushGroup = new THREE.Group();
    const bushGeometry = new THREE.SphereGeometry(0.4 * scale, 8, 8);
    const bush = createCelMesh(bushGeometry, 0x228B22);
    bush.position.y = 0.3 * scale;
    bush.scale.y = 0.7;
    bushGroup.add(bush);

    // Add some smaller spheres for variety
    const smallBush = createCelMesh(new THREE.SphereGeometry(0.25 * scale, 6, 6), 0x2E7D32);
    smallBush.position.set(0.2 * scale, 0.25 * scale, 0.15 * scale);
    bushGroup.add(smallBush);

    bushGroup.position.set(x, 0, z);
    return bushGroup;
  };

  // Bushes near house
  houseGroup.add(createBush(-2.8, 1.5, 1.2));
  houseGroup.add(createBush(2.8, 1.5, 1.0));
  houseGroup.add(createBush(-2.9, -0.5, 0.8));
  houseGroup.add(createBush(2.9, -0.5, 0.9));

  // Position the entire house
  houseGroup.position.copy(position);

  // Add to scene
  scene.add(houseGroup);

  // Return the house object with its components (door mesh for interaction)
  const doorMesh = doorGroup.children.find(child => child.geometry?.parameters?.width === 1.2);
  return {
    group: houseGroup,
    door: doorMesh || door,
    position: position
  };
}

// Create the interior scene - Cozy cottage style (larger)
export function createHouseInterior(scene) {
  const interiorGroup = new THREE.Group();
  interiorGroup.visible = false; // Hidden by default

  // Room dimensions (bigger!)
  const roomWidth = 16;
  const roomDepth = 14;
  const roomHeight = 5;

  // === FLOOR ===
  // Main wooden floor
  const floorGeometry = new THREE.PlaneGeometry(roomWidth, roomDepth);
  const floor = createCelMesh(floorGeometry, 0xDEB887); // Burlywood
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = 0.01;
  interiorGroup.add(floor);

  // Floor planks pattern (darker lines)
  for (let i = -roomWidth/2 + 1; i < roomWidth/2; i += 1) {
    const plankLine = createCelMesh(new THREE.BoxGeometry(0.02, 0.01, roomDepth), 0xA0522D);
    plankLine.position.set(i, 0.02, 0);
    interiorGroup.add(plankLine);
  }

  // Rug in center
  const rugGeometry = new THREE.PlaneGeometry(5, 4);
  const rug = createCelMesh(rugGeometry, 0x8B0000); // Dark red
  rug.rotation.x = -Math.PI / 2;
  rug.position.set(0, 0.03, 1);
  interiorGroup.add(rug);

  // Rug border
  const rugBorder = createCelMesh(new THREE.PlaneGeometry(5.4, 4.4), 0xFFD700);
  rugBorder.rotation.x = -Math.PI / 2;
  rugBorder.position.set(0, 0.025, 1);
  interiorGroup.add(rugBorder);

  // === WALLS ===
  const wallColor = 0xFFF8DC; // Cornsilk
  const createWall = (width, height, x, y, z, rotY = 0) => {
    const wall = createCelMesh(new THREE.PlaneGeometry(width, height), wallColor);
    wall.position.set(x, y, z);
    wall.rotation.y = rotY;
    return wall;
  };

  // Back wall
  interiorGroup.add(createWall(roomWidth, roomHeight, 0, roomHeight/2, -roomDepth/2, 0));

  // Left wall
  interiorGroup.add(createWall(roomDepth, roomHeight, -roomWidth/2, roomHeight/2, 0, Math.PI/2));

  // Right wall
  interiorGroup.add(createWall(roomDepth, roomHeight, roomWidth/2, roomHeight/2, 0, -Math.PI/2));

  // Front wall (with door opening)
  interiorGroup.add(createWall(5, roomHeight, -5.5, roomHeight/2, roomDepth/2, Math.PI));
  interiorGroup.add(createWall(5, roomHeight, 5.5, roomHeight/2, roomDepth/2, Math.PI));
  interiorGroup.add(createWall(6, 2, 0, roomHeight - 1, roomDepth/2, Math.PI)); // Above door

  // === CEILING REMOVED FOR VISIBILITY ===
  // (Ceiling and beams removed to allow top-down view)

  // === WAINSCOTING (lower wall panels) ===
  const wainscotColor = 0xD2B48C;
  const wainscotHeight = 1.2;

  // Back wainscot
  const backWainscot = createCelMesh(new THREE.BoxGeometry(roomWidth - 0.1, wainscotHeight, 0.1), wainscotColor);
  backWainscot.position.set(0, wainscotHeight/2, -roomDepth/2 + 0.05);
  interiorGroup.add(backWainscot);

  // Side wainscots
  const leftWainscot = createCelMesh(new THREE.BoxGeometry(0.1, wainscotHeight, roomDepth - 0.1), wainscotColor);
  leftWainscot.position.set(-roomWidth/2 + 0.05, wainscotHeight/2, 0);
  interiorGroup.add(leftWainscot);

  const rightWainscot = createCelMesh(new THREE.BoxGeometry(0.1, wainscotHeight, roomDepth - 0.1), wainscotColor);
  rightWainscot.position.set(roomWidth/2 - 0.05, wainscotHeight/2, 0);
  interiorGroup.add(rightWainscot);

  // === CORNER POSTS ===
  const createCornerPost = (x, z) => {
    const post = createCelMesh(new THREE.BoxGeometry(0.3, roomHeight, 0.3), 0x5D4037);
    post.position.set(x, roomHeight/2, z);
    interiorGroup.add(post);
  };

  createCornerPost(-roomWidth/2 + 0.15, -roomDepth/2 + 0.15);
  createCornerPost(roomWidth/2 - 0.15, -roomDepth/2 + 0.15);
  createCornerPost(-roomWidth/2 + 0.15, roomDepth/2 - 0.15);
  createCornerPost(roomWidth/2 - 0.15, roomDepth/2 - 0.15);

  // === WINDOWS ===
  const createInteriorWindow = (x, z, rotY = 0) => {
    const windowGroup = new THREE.Group();

    // Window frame
    const frame = createCelMesh(new THREE.BoxGeometry(2, 1.8, 0.15), 0x5D4037);
    windowGroup.add(frame);

    // Window glass (bright, like outside light)
    const glassMaterial = new THREE.MeshStandardMaterial({
      color: 0x87CEEB,
      emissive: 0xFFFFCC,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.6
    });
    const glass = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 1.5), glassMaterial);
    glass.position.z = 0.08;
    windowGroup.add(glass);

    // Window cross
    const vertBar = createCelMesh(new THREE.BoxGeometry(0.08, 1.5, 0.1), 0xF5F5DC);
    vertBar.position.z = 0.1;
    windowGroup.add(vertBar);

    const horizBar = createCelMesh(new THREE.BoxGeometry(1.7, 0.08, 0.1), 0xF5F5DC);
    horizBar.position.z = 0.1;
    windowGroup.add(horizBar);

    // Curtains
    const curtainGeometry = new THREE.PlaneGeometry(0.5, 1.6);
    const curtainMaterial = new THREE.MeshStandardMaterial({ color: 0x8B0000, side: THREE.DoubleSide });

    const leftCurtain = new THREE.Mesh(curtainGeometry, curtainMaterial);
    leftCurtain.position.set(-0.9, 0, 0.12);
    leftCurtain.rotation.y = 0.3;
    windowGroup.add(leftCurtain);

    const rightCurtain = new THREE.Mesh(curtainGeometry, curtainMaterial);
    rightCurtain.position.set(0.9, 0, 0.12);
    rightCurtain.rotation.y = -0.3;
    windowGroup.add(rightCurtain);

    windowGroup.position.set(x, 2.5, z);
    windowGroup.rotation.y = rotY;

    return windowGroup;
  };

  // Add windows
  interiorGroup.add(createInteriorWindow(-roomWidth/2 + 0.1, -3, Math.PI/2));
  interiorGroup.add(createInteriorWindow(-roomWidth/2 + 0.1, 3, Math.PI/2));
  interiorGroup.add(createInteriorWindow(roomWidth/2 - 0.1, -3, -Math.PI/2));
  interiorGroup.add(createInteriorWindow(roomWidth/2 - 0.1, 3, -Math.PI/2));

  // === FIREPLACE (Back wall, center-right) ===
  const fireplaceGroup = new THREE.Group();

  // Stone surround
  const stoneColor = 0x696969;
  const fireplaceBack = createCelMesh(new THREE.BoxGeometry(3.5, 3.5, 0.3), stoneColor);
  fireplaceBack.position.y = 1.75;
  fireplaceGroup.add(fireplaceBack);

  // Fireplace opening
  const opening = createCelMesh(new THREE.BoxGeometry(2, 2, 0.4), 0x1A1A1A);
  opening.position.set(0, 1, 0.1);
  fireplaceGroup.add(opening);

  // Mantel
  const mantel = createCelMesh(new THREE.BoxGeometry(4, 0.25, 0.8), 0x8B4513);
  mantel.position.set(0, 2.5, 0.3);
  fireplaceGroup.add(mantel);

  // Mantel decorations
  const clock = createCelMesh(new THREE.CylinderGeometry(0.3, 0.3, 0.1, 16), 0xFFD700);
  clock.position.set(0, 2.75, 0.3);
  clock.rotation.x = Math.PI/2;
  fireplaceGroup.add(clock);

  // Candlesticks on mantel
  const createCandlestick = (x) => {
    const group = new THREE.Group();
    const base = createCelMesh(new THREE.CylinderGeometry(0.08, 0.1, 0.3, 8), 0xB8860B);
    base.position.y = 0.15;
    group.add(base);
    const candle = createCelMesh(new THREE.CylinderGeometry(0.04, 0.04, 0.25, 8), 0xFFFACD);
    candle.position.y = 0.4;
    group.add(candle);
    const flame = createCelMesh(new THREE.ConeGeometry(0.03, 0.08, 6), 0xFF6600);
    flame.position.y = 0.56;
    group.add(flame);
    group.position.set(x, 2.62, 0.3);
    return group;
  };

  fireplaceGroup.add(createCandlestick(-1.2));
  fireplaceGroup.add(createCandlestick(1.2));

  // Fire logs
  const log1 = createCelMesh(new THREE.CylinderGeometry(0.12, 0.12, 1.2, 8), 0x3E2723);
  log1.position.set(-0.2, 0.3, 0.15);
  log1.rotation.z = Math.PI/2;
  log1.rotation.y = 0.3;
  fireplaceGroup.add(log1);

  const log2 = createCelMesh(new THREE.CylinderGeometry(0.1, 0.1, 1, 8), 0x4E342E);
  log2.position.set(0.15, 0.35, 0.2);
  log2.rotation.z = Math.PI/2;
  log2.rotation.y = -0.2;
  fireplaceGroup.add(log2);

  // Fire glow
  const fireLight = new THREE.PointLight(0xFF4500, 2, 8);
  fireLight.position.set(0, 0.8, 0.5);
  fireplaceGroup.add(fireLight);

  // Animate fire
  const animateFire = () => {
    fireLight.intensity = 1.5 + Math.random() * 1;
    setTimeout(animateFire, 100 + Math.random() * 150);
  };
  animateFire();

  fireplaceGroup.position.set(3, 0, -roomDepth/2 + 0.2);
  interiorGroup.add(fireplaceGroup);

  // === COUCH (Near fireplace) ===
  const couchGroup = new THREE.Group();

  // Couch base
  const couchBase = createCelMesh(new THREE.BoxGeometry(3, 0.5, 1.2), 0x5D4037);
  couchBase.position.y = 0.25;
  couchGroup.add(couchBase);

  // Cushions
  const cushionColor = 0x8B0000; // Dark red to match rug
  const backCushion = createCelMesh(new THREE.BoxGeometry(2.8, 0.8, 0.3), cushionColor);
  backCushion.position.set(0, 0.9, -0.4);
  couchGroup.add(backCushion);

  const seatCushion = createCelMesh(new THREE.BoxGeometry(2.8, 0.2, 0.9), cushionColor);
  seatCushion.position.set(0, 0.6, 0.1);
  couchGroup.add(seatCushion);

  // Armrests
  const armrest1 = createCelMesh(new THREE.BoxGeometry(0.3, 0.6, 1.2), 0x5D4037);
  armrest1.position.set(-1.35, 0.55, 0);
  couchGroup.add(armrest1);

  const armrest2 = createCelMesh(new THREE.BoxGeometry(0.3, 0.6, 1.2), 0x5D4037);
  armrest2.position.set(1.35, 0.55, 0);
  couchGroup.add(armrest2);

  // Pillows
  const pillow1 = createCelMesh(new THREE.BoxGeometry(0.5, 0.5, 0.15), 0xFFD700);
  pillow1.position.set(-0.8, 0.85, 0.2);
  pillow1.rotation.z = 0.2;
  couchGroup.add(pillow1);

  const pillow2 = createCelMesh(new THREE.BoxGeometry(0.5, 0.5, 0.15), 0x228B22);
  pillow2.position.set(0.8, 0.85, 0.2);
  pillow2.rotation.z = -0.2;
  couchGroup.add(pillow2);

  couchGroup.position.set(3, 0, -2);
  couchGroup.rotation.y = Math.PI;
  interiorGroup.add(couchGroup);

  // === COFFEE TABLE ===
  const coffeeTableGroup = new THREE.Group();

  const tableTop = createCelMesh(new THREE.BoxGeometry(1.5, 0.1, 0.8), 0x8B4513);
  tableTop.position.y = 0.5;
  coffeeTableGroup.add(tableTop);

  // Table legs
  const legGeometry = new THREE.BoxGeometry(0.1, 0.5, 0.1);
  const positions = [[-0.6, 0.25, -0.3], [0.6, 0.25, -0.3], [-0.6, 0.25, 0.3], [0.6, 0.25, 0.3]];
  positions.forEach(pos => {
    const leg = createCelMesh(legGeometry, 0x5D4037);
    leg.position.set(...pos);
    coffeeTableGroup.add(leg);
  });

  // Items on coffee table
  const book = createCelMesh(new THREE.BoxGeometry(0.3, 0.05, 0.4), 0x8B0000);
  book.position.set(-0.3, 0.58, 0);
  coffeeTableGroup.add(book);

  const cup = createCelMesh(new THREE.CylinderGeometry(0.08, 0.06, 0.15, 8), 0xF5F5DC);
  cup.position.set(0.3, 0.62, 0.1);
  coffeeTableGroup.add(cup);

  coffeeTableGroup.position.set(3, 0, 0);
  interiorGroup.add(coffeeTableGroup);

  // === ARMCHAIR ===
  const createArmchair = (x, z, rotY) => {
    const chairGroup = new THREE.Group();

    const base = createCelMesh(new THREE.BoxGeometry(1.2, 0.4, 1), 0x5D4037);
    base.position.y = 0.2;
    chairGroup.add(base);

    const seat = createCelMesh(new THREE.BoxGeometry(1, 0.15, 0.8), 0x228B22);
    seat.position.set(0, 0.47, 0.05);
    chairGroup.add(seat);

    const back = createCelMesh(new THREE.BoxGeometry(1, 0.8, 0.2), 0x228B22);
    back.position.set(0, 0.8, -0.35);
    chairGroup.add(back);

    const arm1 = createCelMesh(new THREE.BoxGeometry(0.2, 0.4, 0.9), 0x5D4037);
    arm1.position.set(-0.5, 0.6, 0);
    chairGroup.add(arm1);

    const arm2 = createCelMesh(new THREE.BoxGeometry(0.2, 0.4, 0.9), 0x5D4037);
    arm2.position.set(0.5, 0.6, 0);
    chairGroup.add(arm2);

    chairGroup.position.set(x, 0, z);
    chairGroup.rotation.y = rotY;
    return chairGroup;
  };

  interiorGroup.add(createArmchair(5.5, -1, -Math.PI/4));

  // === DINING AREA (Left side) ===
  const diningGroup = new THREE.Group();

  // Dining table
  const diningTableTop = createCelMesh(new THREE.BoxGeometry(3, 0.12, 1.8), 0x8B4513);
  diningTableTop.position.y = 1;
  diningGroup.add(diningTableTop);

  // Table legs
  const diningLegPositions = [[-1.3, 0.5, -0.7], [1.3, 0.5, -0.7], [-1.3, 0.5, 0.7], [1.3, 0.5, 0.7]];
  diningLegPositions.forEach(pos => {
    const leg = createCelMesh(new THREE.BoxGeometry(0.15, 1, 0.15), 0x5D4037);
    leg.position.set(...pos);
    diningGroup.add(leg);
  });

  // Dining chairs
  const createDiningChair = (x, z, rotY) => {
    const chair = new THREE.Group();

    const seat = createCelMesh(new THREE.BoxGeometry(0.6, 0.08, 0.6), 0x8B4513);
    seat.position.y = 0.55;
    chair.add(seat);

    const back = createCelMesh(new THREE.BoxGeometry(0.6, 0.8, 0.08), 0x8B4513);
    back.position.set(0, 0.95, -0.26);
    chair.add(back);

    // Chair legs
    const chairLegPos = [[-0.22, 0.275, -0.22], [0.22, 0.275, -0.22], [-0.22, 0.275, 0.22], [0.22, 0.275, 0.22]];
    chairLegPos.forEach(pos => {
      const leg = createCelMesh(new THREE.CylinderGeometry(0.03, 0.03, 0.55, 6), 0x5D4037);
      leg.position.set(...pos);
      chair.add(leg);
    });

    chair.position.set(x, 0, z);
    chair.rotation.y = rotY;
    return chair;
  };

  diningGroup.add(createDiningChair(0, -1.2, 0));
  diningGroup.add(createDiningChair(0, 1.2, Math.PI));
  diningGroup.add(createDiningChair(-1.2, 0, Math.PI/2));
  diningGroup.add(createDiningChair(1.2, 0, -Math.PI/2));

  // Table settings
  const createPlaceSetting = (x, z) => {
    const group = new THREE.Group();
    const plate = createCelMesh(new THREE.CylinderGeometry(0.2, 0.2, 0.02, 12), 0xF5F5F5);
    plate.position.y = 1.07;
    group.add(plate);
    group.position.set(x, 0, z);
    return group;
  };

  diningGroup.add(createPlaceSetting(0, -0.5));
  diningGroup.add(createPlaceSetting(0, 0.5));
  diningGroup.add(createPlaceSetting(-0.8, 0));
  diningGroup.add(createPlaceSetting(0.8, 0));

  // Centerpiece (vase with flowers)
  const vase = createCelMesh(new THREE.CylinderGeometry(0.1, 0.15, 0.4, 8), 0x4169E1);
  vase.position.set(0, 1.25, 0);
  diningGroup.add(vase);

  // Flowers
  for (let i = 0; i < 5; i++) {
    const stem = createCelMesh(new THREE.CylinderGeometry(0.01, 0.01, 0.4, 4), 0x228B22);
    stem.position.set((Math.random() - 0.5) * 0.15, 1.55, (Math.random() - 0.5) * 0.15);
    stem.rotation.x = (Math.random() - 0.5) * 0.3;
    stem.rotation.z = (Math.random() - 0.5) * 0.3;
    diningGroup.add(stem);

    const flower = createCelMesh(new THREE.SphereGeometry(0.06, 6, 6), [0xFF69B4, 0xFFD700, 0xFF6347][i % 3]);
    flower.position.set(stem.position.x, 1.75 + Math.random() * 0.1, stem.position.z);
    diningGroup.add(flower);
  }

  diningGroup.position.set(-5, 0, -3);
  interiorGroup.add(diningGroup);

  // === KITCHEN AREA (Left side, front) ===
  const kitchenGroup = new THREE.Group();

  // Kitchen counter
  const counter = createCelMesh(new THREE.BoxGeometry(4, 1.1, 0.8), 0xD2B48C);
  counter.position.set(0, 0.55, 0);
  kitchenGroup.add(counter);

  // Counter top
  const counterTop = createCelMesh(new THREE.BoxGeometry(4.1, 0.08, 0.9), 0x696969);
  counterTop.position.set(0, 1.14, 0);
  kitchenGroup.add(counterTop);

  // Cabinets below
  for (let i = -1.5; i <= 1.5; i += 1) {
    const cabinet = createCelMesh(new THREE.BoxGeometry(0.9, 0.8, 0.7), 0x8B4513);
    cabinet.position.set(i, 0.4, 0);
    kitchenGroup.add(cabinet);

    // Cabinet handle
    const handle = createCelMesh(new THREE.BoxGeometry(0.2, 0.04, 0.04), 0xFFD700);
    handle.position.set(i, 0.5, 0.37);
    kitchenGroup.add(handle);
  }

  // Upper cabinets
  for (let i = -1.5; i <= 1.5; i += 1) {
    const upperCabinet = createCelMesh(new THREE.BoxGeometry(0.9, 0.9, 0.5), 0x8B4513);
    upperCabinet.position.set(i, 2.2, -0.15);
    kitchenGroup.add(upperCabinet);

    const upperHandle = createCelMesh(new THREE.BoxGeometry(0.2, 0.04, 0.04), 0xFFD700);
    upperHandle.position.set(i, 2.0, 0.12);
    kitchenGroup.add(upperHandle);
  }

  // Sink
  const sink = createCelMesh(new THREE.BoxGeometry(0.7, 0.15, 0.5), 0xC0C0C0);
  sink.position.set(0, 1.1, 0.15);
  kitchenGroup.add(sink);

  // Faucet
  const faucetBase = createCelMesh(new THREE.CylinderGeometry(0.03, 0.03, 0.3, 8), 0xC0C0C0);
  faucetBase.position.set(0, 1.35, -0.1);
  kitchenGroup.add(faucetBase);

  const faucetSpout = createCelMesh(new THREE.BoxGeometry(0.04, 0.04, 0.2), 0xC0C0C0);
  faucetSpout.position.set(0, 1.48, 0);
  kitchenGroup.add(faucetSpout);

  // Kitchen items
  const pot = createCelMesh(new THREE.CylinderGeometry(0.15, 0.12, 0.2, 12), 0x2F4F4F);
  pot.position.set(1.2, 1.28, 0.1);
  kitchenGroup.add(pot);

  const kettle = createCelMesh(new THREE.SphereGeometry(0.12, 8, 8), 0xB22222);
  kettle.position.set(-1.2, 1.26, 0.1);
  kettle.scale.y = 0.8;
  kitchenGroup.add(kettle);

  kitchenGroup.position.set(-5.5, 0, 4);
  kitchenGroup.rotation.y = Math.PI/2;
  interiorGroup.add(kitchenGroup);

  // === BEDROOM AREA (Back right corner) ===
  const bedroomGroup = new THREE.Group();

  // Bed
  const bedFrame = createCelMesh(new THREE.BoxGeometry(2.5, 0.5, 3.5), 0x5D4037);
  bedFrame.position.y = 0.25;
  bedroomGroup.add(bedFrame);

  // Headboard
  const headboard = createCelMesh(new THREE.BoxGeometry(2.5, 1.5, 0.2), 0x5D4037);
  headboard.position.set(0, 1, -1.65);
  bedroomGroup.add(headboard);

  // Mattress
  const mattress = createCelMesh(new THREE.BoxGeometry(2.3, 0.3, 3.2), 0xF5F5F5);
  mattress.position.y = 0.65;
  bedroomGroup.add(mattress);

  // Blanket
  const blanket = createCelMesh(new THREE.BoxGeometry(2.2, 0.15, 2.5), 0x4169E1);
  blanket.position.set(0, 0.85, 0.3);
  bedroomGroup.add(blanket);

  // Pillows
  const pillow1Bed = createCelMesh(new THREE.BoxGeometry(0.8, 0.2, 0.5), 0xF5F5F5);
  pillow1Bed.position.set(-0.5, 0.9, -1.2);
  bedroomGroup.add(pillow1Bed);

  const pillow2Bed = createCelMesh(new THREE.BoxGeometry(0.8, 0.2, 0.5), 0xF5F5F5);
  pillow2Bed.position.set(0.5, 0.9, -1.2);
  bedroomGroup.add(pillow2Bed);

  // Nightstand
  const nightstand = createCelMesh(new THREE.BoxGeometry(0.6, 0.6, 0.5), 0x8B4513);
  nightstand.position.set(1.6, 0.3, -1);
  bedroomGroup.add(nightstand);

  // Lamp on nightstand
  const lampBase = createCelMesh(new THREE.CylinderGeometry(0.1, 0.12, 0.15, 8), 0xB8860B);
  lampBase.position.set(1.6, 0.68, -1);
  bedroomGroup.add(lampBase);

  const lampShade = createCelMesh(new THREE.CylinderGeometry(0.2, 0.15, 0.25, 8), 0xFFFACD);
  lampShade.position.set(1.6, 0.88, -1);
  bedroomGroup.add(lampShade);

  // Wardrobe
  const wardrobe = createCelMesh(new THREE.BoxGeometry(1.5, 2.5, 0.8), 0x8B4513);
  wardrobe.position.set(-1.8, 1.25, -1.2);
  bedroomGroup.add(wardrobe);

  // Wardrobe doors detail
  const wardrobeDoor1 = createCelMesh(new THREE.BoxGeometry(0.65, 2.3, 0.05), 0x6D4C41);
  wardrobeDoor1.position.set(-2.05, 1.25, -0.75);
  bedroomGroup.add(wardrobeDoor1);

  const wardrobeDoor2 = createCelMesh(new THREE.BoxGeometry(0.65, 2.3, 0.05), 0x6D4C41);
  wardrobeDoor2.position.set(-1.55, 1.25, -0.75);
  bedroomGroup.add(wardrobeDoor2);

  // Wardrobe handles
  const wHandle1 = createCelMesh(new THREE.SphereGeometry(0.04, 6, 6), 0xFFD700);
  wHandle1.position.set(-1.85, 1.25, -0.7);
  bedroomGroup.add(wHandle1);

  const wHandle2 = createCelMesh(new THREE.SphereGeometry(0.04, 6, 6), 0xFFD700);
  wHandle2.position.set(-1.75, 1.25, -0.7);
  bedroomGroup.add(wHandle2);

  bedroomGroup.position.set(-4.5, 0, -4);
  interiorGroup.add(bedroomGroup);

  // === BOOKSHELF (Right wall) ===
  const bookshelfGroup = new THREE.Group();

  const shelfBack = createCelMesh(new THREE.BoxGeometry(2, 3, 0.1), 0x5D4037);
  shelfBack.position.y = 1.5;
  bookshelfGroup.add(shelfBack);

  // Shelves
  for (let y = 0.5; y <= 2.5; y += 0.7) {
    const shelf = createCelMesh(new THREE.BoxGeometry(2, 0.08, 0.5), 0x8B4513);
    shelf.position.set(0, y, 0.2);
    bookshelfGroup.add(shelf);

    // Books on shelf
    const bookColors = [0x8B0000, 0x006400, 0x00008B, 0x8B008B, 0xB8860B, 0x2F4F4F];
    for (let i = -0.7; i <= 0.7; i += 0.25) {
      if (Math.random() > 0.2) {
        const bookHeight = 0.3 + Math.random() * 0.2;
        const book = createCelMesh(
          new THREE.BoxGeometry(0.08, bookHeight, 0.3),
          bookColors[Math.floor(Math.random() * bookColors.length)]
        );
        book.position.set(i, y + bookHeight/2 + 0.04, 0.2);
        book.rotation.y = (Math.random() - 0.5) * 0.1;
        bookshelfGroup.add(book);
      }
    }
  }

  bookshelfGroup.position.set(7.4, 0, -2);
  bookshelfGroup.rotation.y = -Math.PI/2;
  interiorGroup.add(bookshelfGroup);

  // === DECORATIONS ===

  // Picture frames on walls
  const createPictureFrame = (x, y, z, rotY, color) => {
    const frameGroup = new THREE.Group();
    const frame = createCelMesh(new THREE.BoxGeometry(1.2, 0.9, 0.08), 0x8B4513);
    frameGroup.add(frame);
    const picture = createCelMesh(new THREE.PlaneGeometry(1, 0.7), color);
    picture.position.z = 0.05;
    frameGroup.add(picture);
    frameGroup.position.set(x, y, z);
    frameGroup.rotation.y = rotY;
    return frameGroup;
  };

  interiorGroup.add(createPictureFrame(0, 3, -roomDepth/2 + 0.1, 0, 0x87CEEB)); // Landscape
  interiorGroup.add(createPictureFrame(-3, 3, -roomDepth/2 + 0.1, 0, 0x228B22)); // Forest

  // Potted plants
  const createPottedPlant = (x, z, scale = 1) => {
    const plantGroup = new THREE.Group();

    const pot = createCelMesh(new THREE.CylinderGeometry(0.2 * scale, 0.15 * scale, 0.3 * scale, 8), 0xA0522D);
    pot.position.y = 0.15 * scale;
    plantGroup.add(pot);

    // Leaves
    for (let i = 0; i < 6; i++) {
      const leaf = createCelMesh(new THREE.SphereGeometry(0.15 * scale, 6, 6), 0x228B22);
      const angle = (i / 6) * Math.PI * 2;
      leaf.position.set(
        Math.cos(angle) * 0.15 * scale,
        0.4 * scale + Math.random() * 0.1,
        Math.sin(angle) * 0.15 * scale
      );
      leaf.scale.y = 0.6;
      plantGroup.add(leaf);
    }

    plantGroup.position.set(x, 0, z);
    return plantGroup;
  };

  interiorGroup.add(createPottedPlant(6.5, 5, 1.2));
  interiorGroup.add(createPottedPlant(-6.5, 5, 1));
  interiorGroup.add(createPottedPlant(6.5, -5, 0.8));

  // Coat rack near door
  const coatRackGroup = new THREE.Group();
  const rackPole = createCelMesh(new THREE.CylinderGeometry(0.05, 0.08, 2, 8), 0x5D4037);
  rackPole.position.y = 1;
  coatRackGroup.add(rackPole);

  // Hooks
  for (let i = 0; i < 4; i++) {
    const hook = createCelMesh(new THREE.BoxGeometry(0.15, 0.03, 0.03), 0x8B4513);
    hook.position.set(0.1, 1.5 + i * 0.15, 0);
    hook.rotation.z = -0.3;
    coatRackGroup.add(hook);
  }

  coatRackGroup.position.set(5, 0, 5.5);
  interiorGroup.add(coatRackGroup);

  // === LIGHTING ===
  // Main ambient light
  const ambientLight = new THREE.AmbientLight(0xFFF8DC, 0.4);
  interiorGroup.add(ambientLight);

  // Ceiling light
  const ceilingLight = new THREE.PointLight(0xFFE4B5, 0.8, 20);
  ceilingLight.position.set(0, 4.5, 0);
  interiorGroup.add(ceilingLight);

  // Window lights (simulate daylight)
  const windowLights = [
    { x: -roomWidth/2 + 1, z: -3 },
    { x: -roomWidth/2 + 1, z: 3 },
    { x: roomWidth/2 - 1, z: -3 },
    { x: roomWidth/2 - 1, z: 3 }
  ];

  windowLights.forEach(pos => {
    const light = new THREE.SpotLight(0xFFFFCC, 0.5, 10, Math.PI/6, 0.3);
    light.position.set(pos.x, 3, pos.z);
    light.target.position.set(pos.x > 0 ? pos.x - 2 : pos.x + 2, 0, pos.z);
    interiorGroup.add(light);
    interiorGroup.add(light.target);
  });

  // Add to scene
  scene.add(interiorGroup);

  return interiorGroup;
}

// Create the iris wipe transition effect
export function createIrisWipeEffect() {
  const irisGeometry = new THREE.CircleGeometry(1, 32);
  const irisMaterial = new THREE.MeshBasicMaterial({
    color: 0x000000,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0
  });
  
  const iris = new THREE.Mesh(irisGeometry, irisMaterial);
  iris.scale.set(0.001, 0.001, 0.001); // Start very small
  
  // Create a scene just for the iris effect
  const irisScene = new THREE.Scene();
  const irisCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
  irisCamera.position.z = 1;
  irisScene.add(iris);
  
  return {
    scene: irisScene,
    camera: irisCamera,
    iris: iris,
    material: irisMaterial
  };
}

// Function to handle the transition between exterior and interior
export function transitionToInterior(renderer, mainScene, mainCamera, interiorScene, irisEffect, onComplete) {
  const duration = 1500; // Increased to 1.5 seconds for a more noticeable transition
  const startTime = Date.now();
  
  // Make the interior scene visible but don't render it yet
  interiorScene.visible = true;
  
  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // First half: grow the iris to cover the screen
    if (progress < 0.5) {
      const scale = progress * 4; // Grow from 0 to 2
      irisEffect.iris.scale.set(scale, scale, 1);
      irisEffect.material.opacity = progress * 2; // Fade in
      
      // Render the main scene with the iris overlay
      renderer.autoClear = false;
      renderer.clear();
      renderer.render(mainScene, mainCamera);
      renderer.render(irisEffect.scene, irisEffect.camera);
    } 
    // Second half: shrink the iris to reveal the interior
    else {
      const scale = (1 - (progress - 0.5) * 2) * 4; // Shrink from 2 to 0
      irisEffect.iris.scale.set(scale, scale, 1);
      
      // Render the interior scene with the iris overlay
      renderer.autoClear = false;
      renderer.clear();
      renderer.render(interiorScene, mainCamera);
      renderer.render(irisEffect.scene, irisEffect.camera);
    }
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      // Transition complete
      irisEffect.material.opacity = 0;
      mainScene.visible = false;
      // Make sure interior scene stays visible
      interiorScene.visible = true;
      if (onComplete) onComplete();
    }
  };
  
  animate();
}

// Function to handle the transition from interior back to exterior
export function transitionToExterior(renderer, mainScene, mainCamera, interiorScene, irisEffect, onComplete) {
  const duration = 1500; // Increased to 1.5 seconds for a more noticeable transition
  const startTime = Date.now();
  
  // Make the main scene visible but don't render it yet
  mainScene.visible = true;
  
  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // First half: grow the iris to cover the screen
    if (progress < 0.5) {
      const scale = progress * 4; // Grow from 0 to 2
      irisEffect.iris.scale.set(scale, scale, 1);
      irisEffect.material.opacity = progress * 2; // Fade in
      
      // Render the interior scene with the iris overlay
      renderer.autoClear = false;
      renderer.clear();
      renderer.render(interiorScene, mainCamera);
      renderer.render(irisEffect.scene, irisEffect.camera);
    } 
    // Second half: shrink the iris to reveal the exterior
    else {
      const scale = (1 - (progress - 0.5) * 2) * 4; // Shrink from 2 to 0
      irisEffect.iris.scale.set(scale, scale, 1);
      
      // Render the main scene with the iris overlay
      renderer.autoClear = false;
      renderer.clear();
      renderer.render(mainScene, mainCamera);
      renderer.render(irisEffect.scene, irisEffect.camera);
    }
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      // Transition complete
      irisEffect.material.opacity = 0;
      interiorScene.visible = false;
      // Make sure main scene stays visible
      mainScene.visible = true;
      if (onComplete) onComplete();
    }
  };
  
  animate();
}